import { createFileRoute } from "@tanstack/react-router";
import { ui } from "@patient-watch/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import { useSelectClinicians } from "@/features/admin/api/selectClinicians";
import {
    ChurnRiskBadge,
    getClinicianDiaryCounts,
    NewDiariesCell,
    SparkChart,
    TotalDiariesCell,
} from "@/features/admin/components/shared";
import { calculateChurnRisk } from "@/features/admin/api/adminStats";
import Spinner from "@/components/common/Spinner";
import { Copy } from "@/components/common/Copy";
import { useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import type { SortingState } from "@tanstack/react-table";
import { useNav } from "@/utils/nav";

export const Route = createFileRoute("/_app/customers/churn")({
    component: ChurnPage,
});

function ChurnPage() {
    const { data, isLoading, error } = useSelectClinicians();
    const [sorting, setSorting] = useState<SortingState>([]);

    const toggleSort = (columnId: string) => {
        setSorting((prev) => {
            const existingSort = prev.find((s) => s.id === columnId);
            if (existingSort) {
                // If already sorting by this column, toggle direction
                if (existingSort.desc) {
                    // Remove sorting
                    return [];
                }
                // Toggle to descending
                return [{ id: columnId, desc: true }];
            }
            // Start sorting ascending
            return [{ id: columnId, desc: false }];
        });
    };

    const getSortIcon = (columnId: string) => {
        const sort = sorting.find((s) => s.id === columnId);
        if (!sort) return <ArrowUpDown className="ml-2 w-4 h-4" />;
        return sort.desc ? <ArrowDown className="ml-2 w-4 h-4" /> : <ArrowUp className="ml-2 w-4 h-4" />;
    };

    // Sort clinicians based on current sorting state
    const sortedClinicians = (data || []).sort((a, b) => {
        if (sorting.length === 0) return 0;

        const sort = sorting[0];
        if (!sort) return 0;
        const sortOrder = sort.desc ? -1 : 1;

        switch (sort.id) {
            case "email":
                return (a.email || "").localeCompare(b.email || "") * sortOrder;
            case "total_diaries": {
                const countA = getClinicianDiaryCounts(a).totalDiaries;
                const countB = getClinicianDiaryCounts(b).totalDiaries;
                return (countA - countB) * sortOrder;
            }
            case "new_diaries": {
                const newA = getClinicianDiaryCounts(a).newDiaries14d;
                const newB = getClinicianDiaryCounts(b).newDiaries14d;
                return (newA - newB) * sortOrder;
            }
            case "churn_risk": {
                const riskOrder: Record<string, number> = {
                    churned: 4,
                    high: 3,
                    medium: 2,
                    low: 1,
                };
                const riskA = calculateChurnRisk(a.clinician_id, a.clinician_statistics, a.last_sign_in_at);
                const riskB = calculateChurnRisk(b.clinician_id, b.clinician_statistics, b.last_sign_in_at);
                return ((riskOrder[riskA || ""] || 0) - (riskOrder[riskB || ""] || 0)) * sortOrder;
            }
            case "last_sign_in": {
                const dateA = a.last_sign_in_at ? new Date(a.last_sign_in_at).getTime() : 0;
                const dateB = b.last_sign_in_at ? new Date(b.last_sign_in_at).getTime() : 0;
                return (dateA - dateB) * sortOrder;
            }
            default:
                return 0;
        }
    });

    useNav([
        { label: "Customers", route: { to: "/customers/success" } },
        { label: "Churn", route: { to: "/customers/churn" } },
    ]);

    return (
        <section className="flex flex-col gap-4 p-4 overflow-x-hidden">
            <ui.Card>
                <ui.CardHeader>
                    <ui.CardTitle>Churn risk</ui.CardTitle>
                    <ui.CardDescription>
                        Monitor clinician churn risk and activity trends to identify at-risk users.
                    </ui.CardDescription>
                </ui.CardHeader>
                <ui.CardContent>
                    {isLoading ? (
                        <div className="flex justify-center items-center h-32">
                            <Spinner />
                        </div>
                    ) : error ? (
                        <div className="text-destructive">Failed to load clinician data</div>
                    ) : (
                        <div className="w-full">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead
                                            className="whitespace-nowrap cursor-pointer select-none"
                                            onClick={() => toggleSort("churn_risk")}
                                        >
                                            <div className="flex items-center">
                                                Churn Risk
                                                {getSortIcon("churn_risk")}
                                            </div>
                                        </TableHead>
                                        <TableHead
                                            className="whitespace-nowrap cursor-pointer select-none"
                                            onClick={() => toggleSort("email")}
                                        >
                                            <div className="flex items-center">
                                                Email
                                                {getSortIcon("email")}
                                            </div>
                                        </TableHead>
                                        <TableHead
                                            className="whitespace-nowrap cursor-pointer select-none"
                                            onClick={() => toggleSort("total_diaries")}
                                        >
                                            <div className="flex items-center">
                                                Total Diaries
                                                {getSortIcon("total_diaries")}
                                            </div>
                                        </TableHead>
                                        <TableHead
                                            className="whitespace-nowrap cursor-pointer select-none"
                                            onClick={() => toggleSort("new_diaries")}
                                        >
                                            <div className="flex items-center">
                                                New Diaries
                                                {getSortIcon("new_diaries")}
                                            </div>
                                        </TableHead>
                                        <TableHead
                                            className="whitespace-nowrap cursor-pointer select-none"
                                            onClick={() => toggleSort("last_sign_in")}
                                        >
                                            <div className="flex items-center">
                                                Last Sign In
                                                {getSortIcon("last_sign_in")}
                                            </div>
                                        </TableHead>
                                        <TableHead className="whitespace-nowrap">Activity Trend</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sortedClinicians.map((clinician) => (
                                        <TableRow key={clinician.clinician_id}>
                                            <TableCell>
                                                <ChurnRiskBadge clinician={clinician} />
                                            </TableCell>
                                            <TableCell className="break-words whitespace-normal">
                                                {clinician.email ? (
                                                    <Copy
                                                        id={`email-${clinician.clinician_id}`}
                                                        value={clinician.email}
                                                    >
                                                        {clinician.email}
                                                    </Copy>
                                                ) : (
                                                    " - "
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <TotalDiariesCell clinician={clinician} />
                                            </TableCell>
                                            <TableCell>
                                                <NewDiariesCell clinician={clinician} />
                                            </TableCell>
                                            <TableCell>
                                                {clinician.last_sign_in_at ? (
                                                    <div className="text-sm">
                                                        {new Date(clinician.last_sign_in_at).toLocaleDateString(
                                                            "en-GB",
                                                            {
                                                                day: "2-digit",
                                                                month: "short",
                                                                year: "numeric",
                                                            }
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted-foreground text-sm">Never</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <SparkChart
                                                    clinicianId={clinician.clinician_id}
                                                    clinicianStats={clinician.clinician_statistics}
                                                    churnRisk={calculateChurnRisk(
                                                        clinician.clinician_id,
                                                        clinician.clinician_statistics,
                                                        clinician.last_sign_in_at
                                                    )}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {sortedClinicians.length === 0 && (
                                <div className="mt-2 text-muted-foreground text-sm">No clinician data found.</div>
                            )}
                        </div>
                    )}
                </ui.CardContent>
            </ui.Card>
        </section>
    );
}
